/**
 * AI accuracy curve — day 0 → submit baseline vs live human package.
 * Plan E / F of Estimation Full Rebuild.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useCbApi } from './cbApi';
import { ActionButton, HeaderButton, fmt, useToast } from './CbUi';

const ACCENT = '#0f766e';

const AiAccuracyCurve = ({ estimate, compact = false }) => {
  const api = useCbApi();
  const { showToast, ToastEl } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!estimate?.id) return;
    setLoading(true);
    try {
      const res = await api.get(`/estimation-ai/curve/${estimate.id}`);
      setData(res.data);
    } catch (err) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api, estimate?.id]);

  useEffect(() => { load(); }, [load]);

  const points = useMemo(() => {
    const curve = data?.curve || [];
    if (!curve.length) return [];
    const maxCost = Math.max(
      ...curve.map((p) => Number(p.total_cost) || 0),
      Number(data?.humanLive?.totalCost) || 0,
      1
    );
    return curve.map((p) => ({
      ...p,
      pct: ((Number(p.total_cost) || 0) / maxCost) * 100,
    }));
  }, [data]);

  const seedNow = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/estimation-ai/seed/${estimate.id}`);
      showToast(res.data?.seed?.seeded
        ? 'AI seeded preliminary Cost Breakdown rows'
        : `AI baseline saved (${res.data?.seed?.reason || 'no sheet overwrite'})`);
      await load();
    } catch (err) {
      showToast(err.response?.data?.message || err.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (!estimate?.id) return null;

  const score = data?.scorecard;
  const human = data?.humanLive;

  return (
    <div style={{
      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
      padding: compact ? 12 : 16, marginBottom: 14,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>AI estimate curve</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>
            Mode: <strong>{data?.mode || '…'}</strong>
            {estimate.seeded_from_ai_at ? ' · seeded' : ' · not seeded yet'}
            {estimate.cb_user_touched_at ? ' · human edits locked against overwrite' : ''}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <HeaderButton onClick={load}>🔄</HeaderButton>
          <ActionButton color={ACCENT} onClick={seedNow} disabled={busy}>
            {busy ? 'Running…' : 'Run AI seed / baseline'}
          </ActionButton>
        </div>
      </div>

      {loading && <div style={{ fontSize: 12, color: '#94a3b8' }}>Loading curve…</div>}

      {!loading && points.length === 0 && (
        <div style={{ fontSize: 13, color: '#64748b' }}>
          No AI snapshots yet. Click <strong>Run AI seed / baseline</strong> to create day zero.
        </div>
      )}

      {points.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 90, marginTop: 8 }}>
          {points.map((p) => (
            <div key={`${p.snapshot_date}-${p.day_index}`} title={`Day ${p.day_index}: ${fmt(p.total_cost, 'currency')}`}
              style={{ flex: 1, minWidth: 8, background: ACCENT, opacity: 0.35 + (p.pct / 200), height: `${Math.max(8, p.pct)}%`, borderRadius: '4px 4px 0 0' }}
            />
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 12, fontSize: 12.5 }}>
        <span>Latest AI: <strong>{fmt(points[points.length - 1]?.total_cost, 'currency')}</strong></span>
        <span>Human live: <strong>{fmt(human?.totalCost, 'currency')}</strong></span>
        {score && (
          <>
            <span>MAPE day 0: <strong>{score.mape_day0 != null ? `${(Number(score.mape_day0) * 100).toFixed(1)}%` : '—'}</strong></span>
            <span>MAPE last: <strong>{score.mape_last != null ? `${(Number(score.mape_last) * 100).toFixed(1)}%` : '—'}</strong></span>
          </>
        )}
      </div>
      {ToastEl}
    </div>
  );
};

export default AiAccuracyCurve;
